import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Product, useProducts } from '@/hooks/warehouse';
import { normalizeText } from '@/utils/shared/stringUtils';
import ProductsTable from './ProductsTable';
import ProductFormModal from './ProductFormModal';
import ProductFilters from '@/components/v2/molecules/admin/tabs/WarehouseTab/ProductFilters';

export interface ProductsListViewRef {
  createNew: () => void;
}

const ProductsListView = forwardRef<ProductsListViewRef>((_, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const { data: products = [], isLoading } = useProducts();

  // Filter states - now using IDs for category and origin
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState('');

  useImperativeHandle(ref, () => ({
    createNew: () => {
      setEditingProduct(null);
      setIsModalOpen(true);
    },
  }));

  // Filtered products
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Search filter (name and description)
      if (searchTerm) {
        const normalizedSearch = normalizeText(searchTerm);
        const normalizedName = normalizeText(product.name);
        const normalizedDesc = normalizeText(product.description || '');
        if (!normalizedName.includes(normalizedSearch) && !normalizedDesc.includes(normalizedSearch)) {
          return false;
        }
      }

      // Category filter (now using category_id)
      if (selectedCategory && selectedCategory !== 'all' && product.category_id !== selectedCategory) {
        return false;
      }

      // Origin filter (now using origin_id)
      if (selectedOrigin && selectedOrigin !== 'all' && product.origin_id !== selectedOrigin) {
        return false;
      }

      return true;
    });
  }, [products, searchTerm, selectedCategory, selectedOrigin]);

  const hasActiveFilters = !!(searchTerm || (selectedCategory && selectedCategory !== 'all') || (selectedOrigin && selectedOrigin !== 'all'));

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedOrigin('');
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-4">
      <ProductFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedOrigin={selectedOrigin}
        setSelectedOrigin={setSelectedOrigin}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
      />

      <ProductsTable
        products={filteredProducts}
        isLoading={isLoading}
        onEdit={handleEdit}
      />

      <ProductFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        product={editingProduct}
      />
    </div>
  );
});

ProductsListView.displayName = 'ProductsListView';

export default ProductsListView;
